
import { Component } from 'vue-property-decorator';
import Editable from '@/mixins/Editable';
import { mixins } from 'vue-class-component';
import VWrapper from '@/components/shared/VWrapper.vue';
import EditOptionLoader from '../../components/option/EditOptionLoader.vue';
import { OptionGroup } from '@/interfaces/models/OptionGroup';
import OptionForm from '@/components/option/OptionForm.vue';
import OptionArticleTable from '@/components/option/article/OptionArticleTable.vue';
import OptionArticleForm from '@/components/option/article/OptionArticleForm.vue';
import { OptionArticle } from '@/interfaces/models/OptionArticle';
import StackedForm from '@/mixins/StackedForm';
import { EditOption as EditOptionMixin } from '@/mixins/EditOption';
import Notification from '@/mixins/Notification';
import { namespace } from 'vuex-class';
import { Tag } from '@/interfaces/models/Tag';
import Filter from '@/interfaces/api/Filter';
import { PrintGroups } from '@/interfaces/models/PrintGroups';
import FormatPriceInObject from '@/mixins/FormatPriceInObject';
import FeatureForm from '@/components/option/FeatureForm.vue';
import EditLocalization from '@/mixins/EditLocalization';
import { Category } from '@/interfaces/models/Category';
import { VList, VNavigationDrawer, VTab } from 'vuetify/lib';
import OptionArticleDataTable from '@/components/option/article/OptionArticleDataTable.vue';
import AvailableHourForm from '@/components/article/form/AvailableHourForm.vue';
import { Constrain } from '@/interfaces/models/Constrain';

const tag = namespace('tag');
const printGroups = namespace('printGroups');
const foodcard = namespace('foodcard');
const venue = namespace('venue');
const constrain = namespace('constrain');

@Component({
  components: {
    FeatureForm,
    OptionArticleForm,
    OptionArticleTable,
    OptionArticleDataTable,
    OptionForm,
    EditOptionLoader,
    VWrapper,
    AvailableHourForm,
  },
})
export default class EditOption extends mixins(
  Editable,
  StackedForm,
  Notification,
  EditOptionMixin,
  FormatPriceInObject,
  EditLocalization,
  VNavigationDrawer,
  VList,
) {
  @tag.Action('setFilter') public setFilter!: (filter: Filter) => void;
  @tag.Action('fetch') public getTags!: any;
  @tag.State('items') public tags!: Tag[];

  @printGroups.Action('setFilter') public setPrintGroupsFilter!: (filter: Filter) => void;
  @printGroups.Action('fetch') public getPrintGroups!: any;
  @printGroups.State('items') public printGroups!: PrintGroups[];

  @foodcard.State('categories') public categories!: Category[];
  @foodcard.Action('setCategoryFilter') public setCategoryFilter!: any;
  @foodcard.Action('fetchCategories') public getCategories!: any;
  @foodcard.Action('setOptionArticleFilter') public setOptionArticleFilter!: any;

  @constrain.State('items') public constrains!: Constrain[];
  @constrain.Action('fetch') public getContrains!: any;
  @constrain.Action('setFilter') public setConstrainFilter!: (f: Filter) => void;

  public tab = 0;
  public $refs!: {
    form: InstanceType<typeof OptionForm> & { getData: () => any; validate: () => Promise<boolean | boolean[]> };
    createOption: InstanceType<typeof OptionArticleForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
      reset: () => void;
    };
    editOption: InstanceType<typeof OptionArticleForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
      reset: () => void;
    };
    availableHours: InstanceType<typeof AvailableHourForm> & { getData: () => any };
  };

  get optionForm() {
    if (this.activeOptionArticle) {
      return this.$refs.editOption;
    }

    return this.$refs.createOption;
  }

  get optionArticles() {
    return [...this.sortedOptionArticles];
  }

  disableSwipe(event: any) {
    event.preventDefault();
  }

  public async mounted(): Promise<void> {
    this.setOptionArticleFilter({});
    this.$startLoading('tags');
    this.$startLoading('constrains');
    if (this.venue && this.venue._id) {
      this.setFilter({ venue: this.venue._id });
      this.setPrintGroupsFilter({ venue: this.venue._id });
      this.setConstrainFilter({ venue: this.venue._id });
      await this.getTags();
      await this.getPrintGroups();
      await this.getContrains();
    }

    this.$stopAllLoading();
  }

  public async fetchCategories() {
    this.setCategoryFilter({ venue: this.venue._id });
    this.$startLoading('category');
    await this.getCategories();
    this.$stopLoading('category');
  }

  public updateProp(data: { original: OptionArticle; field: string; value: string }) {
    const update: any = {};
    update[data.field] = data.value;
    this.updateOptionArticle({ id: data.original._id, group: this.active._id, ...update });
  }

  public updateExternalOrderProvider(data: { original: OptionArticle; update: any }) {
    this.updateOptionArticle({
      id: data.original._id,
      group: this.active._id,
      externalOrderProviderSettings: data.update,
    });
  }

  public saveOptionArticle() {
    this.optionForm.validate().then(async (res: boolean[] | boolean) => {
      if (this.isValid(res)) {
        let reset: boolean = false;
        const data: Partial<OptionArticle> = { group: this.active._id, ...this.optionForm.getData() };
        // TODO - may mutate unexpected fields
        const arr = ['price', 'displayPrice', 'standard', 'inside', 'takeAway', 'delivery'];
        arr.map((type: any) => {
          // mutate merged data
          const res = this.deepSearchItems(data, type, (k: any, v: any) => {
            if (v && v.includes) {
              return v.includes(',');
            }
          });
          res.map((obj: any) => {
            obj[type] = obj[type].replaceAll(',', '.');
          });
        });
        this.$startLoading('updateOption');
        if (this.activeOptionArticle?.name && data?.name) {
          data.name = this.serializeLocalization(this.activeOptionArticle.name, data.name);
        }

        if (this.activeOptionArticle?.description && data?.description) {
          data.description = this.serializeLocalization(this.activeOptionArticle.description, data.description);
        }

        if (this.activeOptionArticle?.shortDescription && data?.shortDescription) {
          data.shortDescription = this.serializeLocalization(
            this.activeOptionArticle.shortDescription,
            data.shortDescription,
          );
        }

        if (this.activeOptionArticle?.info && data?.info) {
          data.info = this.serializeLocalization(this.activeOptionArticle.info, data.info);
        }

        if (this.activeOptionArticle) {
          await this.updateOptionArticle({ ...data, id: this.activeOptionArticle._id });
        } else {
          reset = true;
          this.activeOptionArticle = await this.storeOptionArticle(data);
        }

        if (data.assets && data.assets instanceof File) {
          const formData: FormData = new FormData();
          formData.append('asset', data.assets);
          await this.uploadOptionArticleImage({
            group: this.active._id,
            id: this.activeOptionArticle!._id,
            image: formData,
          });
        }

        if (reset) {
          this.$refs.createOption.reset();
        }

        this.$stopLoading('updateOption');
        this.activeOptionArticle = null;
        this.optionModalVisible = false;
      } else {
        this.notifyError('notification.form');
      }
    });
  }

  public async onSearch(queries: any) {
    this.setOptionArticleFilter({ ...this.optionArticleFilter, ...queries });
  }

  public async onCategoryChange(category: string) {
    this.setOptionArticleFilter({ category, ...this.optionArticleFilter });
  }

  public save() {
    this.$refs.form.validate().then(async (res: boolean[] | boolean) => {
      if (this.isValid(res)) {
        const data: Partial<OptionGroup> = {
          venue: this.venue._id,
          ...this.$refs.form.getData(),
          ...this.$refs.availableHours?.getData(),
        };
        if (this.editing) {
          if (this.active && this.active.name && data.name) {
            data.name = this.serializeLocalization(this.active.name, data.name);
          }
          await this.update({ id: this.id, ...data });
        } else {
          await this.store(data);
        }
        if (!this.active) {
          this.$router.push({ name: 'option.index', query: { ...this.$route.query } });
          return;
        }
        let formData: FormData;

        if (data.backgroundImage && data.backgroundImage instanceof File) {
          formData = new FormData();
          formData.append('asset', data.backgroundImage as Blob);
          await this.uploadImage({ id: this.active._id, image: formData });
        }

        this.$router.go(-1);
      }
    });
  }
}
